import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Typography from '@mui/material/Typography';

import { Icons } from '@/components/icons';
import { SecureImage } from '@/components/secure-image';
import type { PrimaryEntityType } from '@/lib/types';

import type { EventEntityChangeWithFailure } from './event-change-card';

interface CollapsedCardProps {
  entityName: string;
  entityType: PrimaryEntityType;
  existingChange?: EventEntityChangeWithFailure;
  displayImage?: string;
  displayPath?: string;
  onToggle: () => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
}

export function CollapsedCard({ entityName, entityType, existingChange, displayImage, displayPath, onToggle, t }: CollapsedCardProps) {
  return (
    <Card
      onClick={onToggle}
      sx={{
        cursor: 'pointer',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        borderRadius: 3,
        borderColor: existingChange?.failedToSave ? 'error.main' : 'divider',
        borderWidth: existingChange?.failedToSave ? 2 : 1,
        borderStyle: 'solid',
        '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 24px -10px rgba(0,0,0,0.15)' },
      }}
    >
      <CardMedia sx={{ aspectRatio: '1 / 1', position: 'relative', bgcolor: 'action.hover', overflow: 'hidden' }}>
        {displayImage ? (
          <SecureImage
            src={displayImage}
            path={displayPath}
            alt={entityName}
            sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.3s' }}
          />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', opacity: 0.1 }}>
            <Icons.codex width={80} height={80} />
          </Box>
        )}
        <Box sx={{
          position: 'absolute', top: 8, left: 8,
          bgcolor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
          color: 'white', px: 1, py: 0.5, borderRadius: 1,
          fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em',
        }}>
          {entityType}
        </Box>
      </CardMedia>
      <CardContent sx={{ flexGrow: 1, p: 1.5, pb: '12px !important' }}>
        <Typography variant="h3" sx={{ fontSize: '0.95rem', mb: 0.5, color: 'text.primary', fontWeight: 600, lineHeight: 1.2 }}>
          {entityName}
        </Typography>
        {existingChange ? (
          <Typography variant="caption" color="primary.main" sx={{ display: 'block', fontWeight: 500 }}>
            {t(
              existingChange.changes.length === 1 ? 'event_implications.changes_count_one' : 'event_implications.changes_count_other',
              { count: existingChange.changes.length },
            )}
          </Typography>
        ) : (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {t('event_implications.no_changes_detected')}
          </Typography>
        )}
        {existingChange?.failedToSave && (
          <Typography variant="caption" color="error.main" sx={{ display: 'block', fontWeight: 600, mt: 0.5 }}>
            {t('event_implications.save_failed')}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}
